import React, { createContext, useContext, useEffect, useMemo, useRef, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTutorNoShowMachine, isRetryableClaimRejection } from '../../../../engine/live/student/tutorNoShow/useTutorNoShowMachine'
import { TUTOR_NO_SHOW } from '../../../../engine/live/student/tutorNoShow/constants'
import { liveService } from '@data/services/liveService'
import { authService } from '@data/services/authService'
import { useApiStatus } from '@common/hooks/useApiStatus'
import moment from 'moment'
import { findClosestNextOccurrence } from '../../../../student/account/onboarding/LiveRoomCard'
import { setTutorEverPresent, cleanOldTutorPresence } from '@data/redux/actions/liveMode'
import { liveRoomSignalsFor } from '@data/redux/reducers/liveMode'

const TutorNoShowContext = createContext(null)

/**
 * Provider for tutor no-show logic
 * Single source of truth for the entire flow
 */
export function TutorNoShowProvider({
    children,
    liveSession,
    presentTutors,
    isStudentInSession,
    apiRequest,
    apiIdle,
    room,
    navigation,
    setIsLiveModeEnabled,
    skipCleanupRef,
    // Ce provider était monté *conditionnellement* dans `LiveContainerV3`. Comme
    // la condition dépend de données qui arrivent après le premier rendu, sa
    // bascule changeait le type d'élément à cette position : React détruisait
    // tout le sous-arbre live et le reconstruisait, et le démontage de
    // `LKVideoCallPortal` coupait la room LiveKit au passage. Le provider est
    // désormais toujours monté ; c'est ce drapeau qui décide s'il agit.
    enabled = true,
}) {
    // `findClosestNextOccurrence` alloue, filtre et trie en construisant deux
    // objets moment par comparaison. Ce provider étant désormais monté dans
    // toutes les séances, le laisser tourner à chaque rendu ferait payer ce
    // coût aux séances qui ne s'en servent pas.
    const sessionStartTime = useMemo(
        () => findClosestNextOccurrence([liveSession])?.date,
        [liveSession]
    )
    const isTutorPresent = (presentTutors?.length || 0) > 0
    // When the tutor announced a delay, the student is told when they said they
    // would arrive — one sentence, read by every waiting surface of the live.
    const declaredDelayMinutes = liveSession?.delay_today || 0
    const announcedArrival = useMemo(
        () => (declaredDelayMinutes > 0 && sessionStartTime
            ? `Ton tuteur a prévenu : il arrive vers ${moment(sessionStartTime).add(declaredDelayMinutes, 'minutes').format('HH[h]mm')}`
            : null),
        [declaredDelayMinutes, sessionStartTime]
    )
    const waitingMessage = announcedArrival
        ? `${announcedArrival}. En attendant, travaille sur ton activité !`
        : undefined
    const searchTriggeredRef = useRef(false)
    const dispatch = useDispatch()

    // Build session key for Redux persistence
    const sessionKey = liveSession?.id && sessionStartTime
        ? `${liveSession.id}_${moment(sessionStartTime).format('YYYY-MM-DD-HH:mm')}`
        : null

    // Read tutor presence history from Redux (persisted)
    const tutorPresenceHistory = useSelector(state => state.data.tutorPresence?.history || {})
    const hasEverBeenPresent = sessionKey ? !!tutorPresenceHistory[sessionKey] : false

    // Dispatch action to mark tutor as present (persisted in Redux)
    const setTutorPresent = useCallback(() => {
        if (sessionKey) {
            dispatch(setTutorEverPresent(sessionKey))
        }
    }, [sessionKey, dispatch])

    // Clean up old tutor presence records (> 24h) on provider mount.
    // Gardé : c'est une action redux globale (tous les `mapStateToProps` de
    // l'arbre re-tournent) au moment le plus chargé — le montage du live — pour
    // un état qu'une séance inactive n'utilisera jamais.
    useEffect(() => {
        if (!enabled) return
        dispatch(cleanOldTutorPresence())
    }, [enabled, dispatch])

    const machine = useTutorNoShowMachine({
        sessionStartTime,
        isTutorPresent,
        isStudentInSession,
        enabled: enabled && isStudentInSession,
        hasEverBeenPresent,
        setTutorPresent,
        dispatchAfterMinutes: liveSession?.tutor_absence_dispatch_after_minutes,
    })

    // The join and credit buttons mark the student as already out of this live
    // before their request; a refusal that keeps them waiting here undoes it,
    // so leaving later still tells the server.
    const onClaimRejected = useCallback((rejection) => {
        if (isRetryableClaimRejection(rejection) && skipCleanupRef) {
            skipCleanupRef.current = false
        }
        machine.actions.claimRejected(rejection)
    }, [skipCleanupRef, machine.actions.claimRejected])

    // Effect: Trigger search when time threshold reached
    useEffect(() => {
        if (!enabled) return
        if (machine.shouldTriggerSearch && !searchTriggeredRef.current) {
            searchTriggeredRef.current = true
            machine.actions.startSearch()

            const closestNext = findClosestNextOccurrence([liveSession])
            if (!closestNext?.date) {
                console.error('[TutorNoShow] No occurrence date available for search')
                machine.actions.noRecoveryFound()
                return
            }

            const searchParams = {
                live_session_id: liveSession.id,
                occurrence_date: moment(closestNext.date).format('YYYY-MM-DD HH:mm'),
                search_min_date: moment().format('YYYY-MM-DD HH:mm'),
                search_max_date: moment().add(TUTOR_NO_SHOW.SEARCH_WINDOW, 'minutes').format('YYYY-MM-DD HH:mm'),
            }

            console.log('[TutorNoShow] Searching for recovery possibilities:', searchParams)
            apiRequest(liveService.getRecoveryPossibilities, {}, {}, searchParams)
        } else if (machine.status === 'IDLE' || machine.status === 'WAITING') {
            // Re-arm when the tutor arrives, or when a too-early refusal sent
            // the machine back to waiting.
            searchTriggeredRef.current = false
        }
    }, [enabled, machine.shouldTriggerSearch, machine.status, liveSession, apiRequest, machine.actions])

    // Handle recovery search response
    useApiStatus(
        liveService.getRecoveryPossibilities,
        null,
        true,
        (successData) => {
            // `getRecoveryPossibilities` sert aussi au parcours d'absence, hors
            // live : maintenant que ce provider est toujours monté, il ne doit
            // réagir que lorsqu'il est actif.
            if (!enabled) return
            const compatibleSession = successData?.possible_live_occurrences?.[0]

            if (!compatibleSession) {
                console.log('[TutorNoShow] No compatible session found')
                machine.actions.noRecoveryFound()
            } else {
                console.log('[TutorNoShow] Compatible session found:', compatibleSession)
                machine.actions.recoveryFound(compatibleSession)
            }
        },
        (errorData) => {
            console.error('[TutorNoShow] Error searching for recovery:', errorData)
            machine.actions.reportError(errorData)
        }
    )

    // Handle joining recovery session
    useApiStatus(
        liveService.autoRecoveryTutorLate,
        'Tu as été assigné à la nouvelle séance avec succès !',
        false,
        (successData) => {
            if (!enabled) return
            machine.actions.complete()

            // Skip cleanup on unmount since backend handled it
            if (skipCleanupRef) {
                skipCleanupRef.current = true
            }

            // Disconnect from current session (no navigation - recovery session is usually 45min+ later)
            if (room) {
                room.disconnect(true).then(() => {
                    setTimeout(() => {
                        if (setIsLiveModeEnabled) setIsLiveModeEnabled(false)
                        apiRequest(authService.getUser)
                    }, 400)
                })
            } else {
                setTimeout(() => {
                    if (setIsLiveModeEnabled) setIsLiveModeEnabled(false)
                    apiRequest(authService.getUser)
                }, 400)
            }
        },
        (errorData) => {
            console.log("errorData", errorData)
            // Check if slot is no longer available (409 conflict)
            const isSlotUnavailable = errorData?.message === 'SLOT_NO_LONGER_AVAILABLE' ||
                errorData?.response?.status === 409

            if (errorData?.claim_rejected_reason) {
                onClaimRejected(errorData)
                return
            }

            if (isSlotUnavailable) {
                console.log('[TutorNoShow] Slot no longer available, re-triggering search')
                // Reset search flag and re-trigger search for new proposals
                searchTriggeredRef.current = false
                machine.actions.startSearch()

                const closestNext = findClosestNextOccurrence([liveSession])
                if (closestNext?.date) {
                    const searchParams = {
                        live_session_id: liveSession.id,
                        occurrence_date: moment(closestNext.date).format('YYYY-MM-DD HH:mm'),
                        search_min_date: moment().format('YYYY-MM-DD HH:mm'),
                        search_max_date: moment().add(TUTOR_NO_SHOW.SEARCH_WINDOW, 'minutes').format('YYYY-MM-DD HH:mm'),
                    }
                    apiRequest(liveService.getRecoveryPossibilities, {}, {}, searchParams)
                } else {
                    machine.actions.noRecoveryFound()
                }
            } else {
                console.error('[TutorNoShow] Error joining recovery:', errorData)
                machine.actions.reportError(errorData)
            }
        }
    )

    // Handle credit fallback
    useApiStatus(
        liveService.autoRecoveryFallback,
        'Ton crédit a été conservé. Tes parents ont été prévenus.',
        true,
        (successData) => {
            if (!enabled) return
            machine.actions.complete()

            // Skip cleanup on unmount
            if (skipCleanupRef) {
                skipCleanupRef.current = true
            }

            // Disconnect from session
            if (room) {
                room.disconnect(true).then(() => {
                    setTimeout(() => {
                        if (setIsLiveModeEnabled) setIsLiveModeEnabled(false)
                    }, 400)
                })
            } else {
                // No room reference, disconnect via setIsLiveModeEnabled directly
                setTimeout(() => {
                    if (setIsLiveModeEnabled) setIsLiveModeEnabled(false)
                }, 400)
            }
        },
        (errorData) => {
            if (errorData?.claim_rejected_reason) {
                onClaimRejected(errorData)
                return
            }
            console.error('[TutorNoShow] Error processing credit fallback:', errorData)
            machine.actions.reportError(errorData)
        }
    )

    // Le tuteur n'est pas absent : il termine sa séance précédente (le serveur le
    // dit au poll de la salle). Tous les écrans d'attente le disent ; sinon ils
    // gardent leur message par défaut.
    const tutorBusyElsewhere = useSelector(
        (state) => !!liveRoomSignalsFor(state, liveSession?.id)?.tutor_busy_elsewhere
    )

    // Volontairement NON mémoïsé : `useTutorNoShowMachine` rend un objet
    // littéral neuf à chaque rendu, donc un `useMemo` prenant `machine` en
    // dépendance ne ferait jamais mouche — juste du bruit. Stabiliser vraiment
    // ce contexte suppose de mémoïser d'abord le retour de la machine, ce qui
    // sort du périmètre de ce correctif.
    const value = {
        ...machine,
        announcedArrival,
        tutorBusyElsewhere,
        // Seen teaching the previous séance beats an announced delay.
        waitingMessage: tutorBusyElsewhere ? TUTOR_NO_SHOW.WAITING_MESSAGE_BUSY_ELSEWHERE : waitingMessage,
        liveSession,
        room,
        navigation,
        apiRequest,
        skipCleanupRef,
    }

    return (
        <TutorNoShowContext.Provider value={value}>
            {children}
        </TutorNoShowContext.Provider>
    )
}

/**
 * Hook to access tutor no-show context
 * Returns null if provider is not present (e.g., non-group sessions like trials)
 * Safe to call from any component
 */
export const useTutorNoShow = () => {
    const context = useContext(TutorNoShowContext)
    // Return null instead of throwing - allows components to gracefully handle absence
    // This happens in trial sessions where TutorNoShowProvider is not wrapped
    return context || null
}
